import { useState, useMemo, useEffect, useCallback } from 'react';
import moment from 'moment';
import toast from 'react-hot-toast';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import {
  getAvailableActions,
  getStatusColor,
  capitalizeFirst,
} from '../../utils';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  DataTable,
  type DataTableColumn,
} from '../../components/ui/data-table';
import InlineFilter from '../../components/ui/inline-filter';
import ActionDropdown from '../../components/common/action-dropdown';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Users,
  DollarSign,
  TrendingUp,
  UserCheck,
  Download,
  Filter,
  Plus,
} from 'lucide-react';
import { adminAPI } from '../../services/api';
import { CLIENT_FILTER_FIELDS } from '../../constants/filters';
import StatsCard from '../../components/common/StatsCard';
import PageHeader from '../../components/common/PageHeader';
import LoaderOverlay from '../../components/common/LoaderOverlay';
import RejectModal from '../../components/common/RejectModal';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import CreateClientDrawer from '../../components/common/CreateClientDrawer';
import ClientDrawer from '../../components/common/ClientDrawer';

interface Client {
  id: string;
  name: string;
  email: string;
  phone: string;
  status: string;
  balance: string;
  joined: string;
  lastLogin: string;
  createdAt: string;
  role: string;
  emailVerified: boolean;
  phoneVerified: boolean;
  referralCode?: string | null;
}

interface SortConfig {
  key: string;
  direction: 'asc' | 'desc';
}

interface FilterField {
  id: string;
  label: string;
  options: { value: string; label: string }[];
}

interface ClientStats {
  totalClients: number;
  activeClients: number;
  pendingClients: number;
  newRegistration: number;
  totalBalance: number;
}

const getStatusDisplay = (status: string) => {
  const statusMap: { [key: string]: string } = {
    active: 'Active',
    pending: 'Pending',
    inactive: 'Inactive',
  };

  return (
    statusMap[status?.toLowerCase()] || capitalizeFirst(status || 'unknown')
  );
};

const StatusBadge = ({
  status,
  onStatusChange,
  availableActions,
}: {
  status: string;
  onStatusChange: (action: string) => void;
  availableActions: string[];
}) => (
  <div className="flex items-center space-x-2">
    <div className="flex items-center space-x-1">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className={`text-xs font-medium rounded-full ${getStatusColor(status)}`}
          >
            {getStatusDisplay(status)}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-32">
          {availableActions.map(action => (
            <DropdownMenuItem
              key={action}
              onClick={() => onStatusChange(action)}
              className="text-sm"
            >
              {action}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  </div>
);

const Clients = () => {
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: 'createdAt',
    direction: 'desc',
  });
  const [allClients, setAllClients] = useState<Client[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilters, setActiveFilters] = useState<{
    [key: string]: string;
  }>();
  const [showInlineFilters, setShowInlineFilters] = useState(false);
  const filterFields: FilterField[] = CLIENT_FILTER_FIELDS;
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [currentPage] = useState(1);
  const [pageSize] = useState(10);
  const [isSaving] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [stats, setStats] = useState<ClientStats>({
    totalClients: 0,
    activeClients: 0,
    pendingClients: 0,
    newRegistration: 0,
    totalBalance: 0,
  });
  const [clientToDelete, setClientToDelete] = useState<Client | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [createClientDrawerOpen, setCreateClientDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<'view' | 'edit'>('view');
  const [selectedUser, setSelectedUser] = useState<Client | null>(null);
  const [formData, setFormData] = useState<Partial<Client>>({});
  const [isDrawerSaving, setIsDrawerSaving] = useState(false);

  const fetchUsers = useCallback(
    async (
      search?: string,
      status?: string,
      page: number = 1,
      limit: number = 10,
      roleId?: string
    ) => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('Access token required');
        }

        const params: any = {
          limit,
          page,
        };

        if (search && search.trim()) {
          params.search = search.trim();
        }

        if (status && status !== 'all') {
          params.status = status;
        }

        if (roleId) {
          params.roleId = roleId;
        }
        const response = await adminAPI.getUsersList(params);
        let usersData = [];
        if (response && response.data) {
          if (Array.isArray(response.data)) {
            usersData = response.data;
          } else if (
            response.data.success &&
            response.data.data &&
            Array.isArray(response.data.data)
          ) {
            usersData = response.data.data;
          } else if (
            response.data.users &&
            Array.isArray(response.data.users)
          ) {
            usersData = response.data.users;
          } else {
            console.log('Response data structure:', Object.keys(response.data));
          }
        } else if (Array.isArray(response)) {
          usersData = response;
        }

        const statsFromResponse =
          response?.data?.stats || response?.stats || null;

        if (statsFromResponse) {
          setStats({
            totalClients: Number(statsFromResponse.totalClients) || 0,
            activeClients: Number(statsFromResponse.activeClients) || 0,
            pendingClients: Number(statsFromResponse.pendingClients) || 0,
            newRegistration: Number(statsFromResponse.newRegistration) || 0,
            totalBalance: Number(statsFromResponse.totalBalance) || 0,
          });
        }

        const transformedClients = usersData
          .filter((user: unknown) => {
            if (!user || typeof user !== 'object') {
              return false;
            }

            const userObj = user as Record<string, unknown>;

            const userRole =
              (userObj.roleData as Record<string, unknown>)?.name || 'user';
            const isAdmin = String(userRole).toLowerCase() === 'admin';
            return !isAdmin;
          })
          .map((user: unknown): Client => {
            if (!user || typeof user !== 'object') {
              return {
                id: `CLT${Math.random()}`,
                name: 'N/A',
                email: 'N/A',
                phone: 'N/A',
                status: 'pending',
                balance: '$0.00',
                joined: 'N/A',
                lastLogin: '-',
                createdAt: 'N/A',
                role: 'user',
                emailVerified: false,
                phoneVerified: false,
              };
            }

            const userObj = user as Record<string, unknown>;
            const transformedClient = {
              id: userObj.id ? `CLT${userObj.id}` : `CLT${Math.random()}`,
              name: (userObj.fullName as string) || 'N/A',
              email: (userObj.email as string) || 'N/A',
              phone: (userObj.phone as string) || 'N/A',
              status: (userObj.status as string) || 'pending',
              balance: '$0.00',
              joined: userObj.createdAt
                ? moment(userObj.createdAt).format('DD/MM/YYYY')
                : 'N/A',
              lastLogin: userObj.lastLogin
                ? moment(userObj.lastLogin).format('DD/MM/YYYY h:mm a')
                : '-',
              createdAt: (userObj.createdAt as string) || 'N/A',
              role:
                ((userObj.roleData as Record<string, unknown>)
                  ?.name as string) || 'user',
              emailVerified: (userObj.emailVerified as boolean) || false,
              phoneVerified: (userObj.phoneVerified as boolean) || false,
              referralCode: (userObj.referralCode as string | null) || null,
            };
            return transformedClient;
          });

        setAllClients(transformedClients);
      } catch (err: unknown) {
        console.error('Failed to fetch users:', err);
        setAllClients([]);
      }
    },
    []
  );

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleDeleteClient = useCallback(
    (clientId: string) => {
      const client = allClients.find(c => c.id === clientId);
      if (client) {
        setClientToDelete(client);
        setDeleteModalOpen(true);
      }
    },
    [allClients]
  );

  const handleStatusAction = useCallback((client: Client, action: string) => {
    if (action === 'Approve') {
      handleApprove(client);
    } else if (action === 'Reject') {
      handleRejectClick(client);
    }
  }, []);

  const handleFiltersApply = useCallback(
    (filters: { [key: string]: string }) => {
      setActiveFilters(filters);
      let statusFilter = filters.status || '';

      const statusMap: { [key: string]: string } = {
        approve: 'approve',
        reject: 'reject',
        pending: 'pending',
      };

      statusFilter = statusMap[statusFilter] || statusFilter;
      fetchUsers(searchTerm, statusFilter);
    },
    [searchTerm, fetchUsers]
  );

  const handleFiltersClear = useCallback(() => {
    setActiveFilters({});
    fetchUsers(searchTerm, '');
  }, [searchTerm, fetchUsers]);

  const handleSearchReset = useCallback(() => {
    setSearchTerm('');
    setSortConfig({ key: '', direction: 'asc' });

    fetchUsers('', activeFilters?.status || '');
  }, [activeFilters, fetchUsers]);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchTerm(value);

      if (value.length >= 3) {
        const timeoutId = setTimeout(() => {
          fetchUsers(value, activeFilters?.status || '');
        }, 500);

        return () => clearTimeout(timeoutId);
      } else if (value.length === 0) {
        fetchUsers('', activeFilters?.status || '');
      }
    },
    [searchTerm, activeFilters, fetchUsers]
  );

  const filteredAndSearchedClients = useMemo(() => {
    return allClients;
  }, [allClients]);

  const sortedClients = useMemo(() => {
    if (!sortConfig.key) return allClients;

    return [...allClients].sort((a: Client, b: Client) => {
      const aValue = a[sortConfig.key as keyof Client];
      const bValue = b[sortConfig.key as keyof Client];

      const aVal = aValue ?? '';
      const bVal = bValue ?? '';

      if (aVal < bVal) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aVal > bVal) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [filteredAndSearchedClients, sortConfig]);

  const openDrawer = async (clientId: string, mode: 'view' | 'edit') => {
    try {
      setDrawerMode(mode);

      const numericId = clientId.replace(/\D/g, '');
      const response = await adminAPI.getUserById(numericId);

      if (response.success && response.data) {
        const userData = response.data;
        setSelectedUser(userData);
        setFormData(userData);
        setDrawerOpen(true);
      } else {
        toast.error('Failed to fetch user data');
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
      toast.error('Failed to fetch user data');
    }
  };

  const columns = useMemo<DataTableColumn<Client>[]>(
    () => [
      {
        id: 'name',
        header: 'Name',
        accessor: (row: Client) => (
          <div
            className="font-medium text-xs sm:text-sm truncate"
            title={row.name}
          >
            {row.name}
          </div>
        ),
        sortable: true,
        sortValue: (row: Client) => row.name,
        width: '180px',
      },
      {
        id: 'email',
        header: 'Email',
        accessor: (row: Client) => (
          <div className="text-xs sm:text-sm truncate" title={row.email}>
            {row.email}
          </div>
        ),
        sortable: true,
        sortValue: (row: Client) => row.email,
        width: '180px',
      },
      {
        id: 'phone',
        header: 'Phone',
        accessor: (row: Client) => (
          <div className="text-xs sm:text-sm">+{row.phone}</div>
        ),
        sortable: true,
        sortValue: (row: Client) => row.phone,
        width: '180px',
      },
      {
        id: 'status',
        header: 'Status',
        accessor: (row: Client) => (
          <StatusBadge
            status={row.status}
            availableActions={getAvailableActions(row.status)}
            onStatusChange={action => handleStatusAction(row, action)}
          />
        ),
        sortable: true,
        sortValue: (row: Client) => row.status,
        width: '140px',
      },
      {
        id: 'referralCode',
        header: 'IB A/C Holder',
        accessor: (row: Client) => {
          const isIb = Boolean(row.referralCode);
          return (
            <Badge
              className={isIb ? getStatusColor('yes') : getStatusColor('no')}
            >
              {isIb ? 'Yes' : 'No'}
            </Badge>
          );
        },
        sortable: true,
        sortValue: (row: Client) => (row.referralCode ? 'Yes' : 'No'),
        width: '120px',
      },
      {
        id: 'joined',
        header: 'Joined',
        accessor: (row: Client) => (
          <div className="text-xs sm:text-sm">{row.joined}</div>
        ),
        sortable: true,
        sortValue: (row: Client) => row.joined,
        width: '160px',
      },
      {
        id: 'lastLogin',
        header: 'Last Login',
        accessor: (row: Client) => (
          <div className="flex items-center text-xs sm:text-sm text-muted-foreground">
            <span className="truncate" title={row.lastLogin}>
              {row.lastLogin}
            </span>
          </div>
        ),
        sortable: true,
        sortValue: (row: Client) => row.lastLogin,
        width: '180px',
      },
      {
        id: 'actions',
        header: 'Actions',
        accessor: (row: Client) => (
          <ActionDropdown
            id={row.id}
            showView={true}
            showEdit={true}
            showDelete={true}
            onView={() => openDrawer(row.id, 'view')}
            onEdit={() => openDrawer(row.id, 'edit')}
            onDelete={() => handleDeleteClient(row.id)}
          />
        ),
        width: '120px',
      },
    ],
    [handleDeleteClient, handleStatusAction]
  );

  const handleStatusUpdate = async (
    client: Client,
    newStatus: string,
    reason?: string
  ) => {
    try {
      setStatusUpdating(true);

      const payload: { status: string; reason?: string } = {
        status: newStatus,
      };
      if (reason) {
        payload.reason = reason;
      }

      let clientId: string | number = client.id;
      if (typeof client.id === 'string') {
        const numericId = client.id.replace(/\D/g, '');
        if (numericId) {
          clientId = parseInt(numericId, 10);
        }
      }

      await adminAPI.updateUserStatus(clientId, payload);

      const statusFilter = activeFilters?.status || '';
      await fetchUsers(searchTerm, statusFilter, currentPage, pageSize);

      if (rejectModalOpen) {
        setRejectModalOpen(false);
        setRejectReason('');
        setSelectedClient(null);
      }
    } catch (error: unknown) {
      console.error('Failed to update status:', error);

      alert(
        `Failed to update status: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleApprove = (client: Client) => {
    handleStatusUpdate(client, 'approve');
  };

  const handleRejectClick = (client: Client) => {
    setSelectedClient(client);
    setRejectModalOpen(true);
  };

  const handleRejectSubmit = () => {
    if (selectedClient && rejectReason.trim()) {
      handleStatusUpdate(selectedClient, 'reject', rejectReason.trim());
    }
  };

  const handleRejectCancel = () => {
    setRejectModalOpen(false);
    setRejectReason('');
    setSelectedClient(null);
  };

  const handleConfirmDelete = async () => {
    if (!clientToDelete) return;

    try {
      setIsDeleting(true);
      const numericId = clientToDelete.id.replace(/\D/g, '');
      const response = await adminAPI.deleteUser(numericId);

      if (response.success) {
        toast.success(response.message || 'User deleted successfully');
        setDeleteModalOpen(false);
        setClientToDelete(null);
        await fetchUsers(
          searchTerm,
          activeFilters?.status || '',
          currentPage,
          pageSize
        );
      }
    } catch (error: any) {
      console.error('Failed to delete user:', error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to delete user';
      toast.error(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };
  const handlCreateNewClient = () => {
    setCreateClientDrawerOpen(true);
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setClientToDelete(null);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setTimeout(() => {
      setSelectedUser(null);
      setFormData({});
    }, 200);
  };

  const handleSave = async () => {
    if (!selectedUser) return;

    try {
      setIsDrawerSaving(true);

      const numericId = selectedUser.id
        ? String(selectedUser.id).replace(/\D/g, '')
        : '';
      const response = await adminAPI.updateUser(numericId, formData);

      if (response.success) {
        toast.success('User updated successfully');
        handleDrawerClose();
        await fetchUsers(
          searchTerm,
          activeFilters?.status || '',
          currentPage,
          pageSize
        );
      } else {
        toast.error(response.message || 'Failed to update user');
      }
    } catch (error: any) {
      console.error('Failed to update user:', error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to update user';
      toast.error(errorMessage);
    } finally {
      setIsDrawerSaving(false);
    }
  };

  return (
    <>
      <LoaderOverlay
        isLoading={statusUpdating || isSaving || isDrawerSaving}
        loadingText={
          isDrawerSaving
            ? 'Saving...'
            : isSaving
              ? 'Saving...'
              : 'Updating status...'
        }
      >
        <div className="space-y-6">
          <div className="sticky top-0 z-20 bg-background pt-4">
            <PageHeader
              title="Client Management"
              description="Manage and monitor client accounts"
              actionLabel="Add Client"
              actionIcon={Plus}
              onAction={handlCreateNewClient}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            <StatsCard
              title="Total Clients"
              value={stats.totalClients.toLocaleString()}
              icon={Users}
            />
            <StatsCard
              title="Active Clients"
              value={stats.activeClients.toLocaleString()}
              icon={UserCheck}
            />

            <StatsCard
              title="Pending Clients"
              value={stats.pendingClients.toLocaleString()}
              icon={Users}
            />

            <StatsCard
              title="Total Balance"
              value={
                stats.totalBalance > 0
                  ? `$${stats.totalBalance.toLocaleString()}`
                  : '$0'
              }
              icon={DollarSign}
            />

            <StatsCard
              title="New Registrations"
              value={stats.newRegistration.toLocaleString()}
              icon={TrendingUp}
            />
          </div>

          <div className="w-full">
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between gap-4 w-full">
                  <div>
                    <CardTitle>Client List</CardTitle>
                    <CardDescription>
                      Manage and view all client accounts
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowInlineFilters(!showInlineFilters)}
                    >
                      <Filter className="h-4 w-4 mr-2" />
                      {showInlineFilters ? 'Hide Filter' : 'Show Filter'}
                    </Button>
                    <Button variant="outline" size="sm">
                      <Download className="h-4 w-4 mr-2" />
                      Export
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <InlineFilter
                  showFilters={showInlineFilters}
                  onToggle={() => setShowInlineFilters(!showInlineFilters)}
                  filterFields={filterFields}
                  activeFilters={activeFilters || {}}
                  onFilterChange={handleFiltersApply}
                  onFiltersClear={handleFiltersClear}
                  onFiltersApply={handleFiltersApply}
                  title="Filter Clients"
                />
                <DataTable
                  columns={columns}
                  data={sortedClients}
                  initialPageSize={pageSize}
                  initialSortBy={{ id: 'createdAt', desc: true }}
                  searchValue={searchTerm}
                  searchPlaceholder="Search Email, Name, Phone"
                  onSearchChange={handleSearchChange}
                  onSearchReset={handleSearchReset}
                  onAddClick={() => console.log('Add client clicked')}
                  onExportClick={() => console.log('Export clicked')}
                  showColumnVisibility={true}
                />
              </CardContent>
            </Card>
          </div>
          <RejectModal
            isOpen={rejectModalOpen}
            clientName={selectedClient?.name}
            rejectReason={rejectReason}
            onRejectReasonChange={setRejectReason}
            onCancel={handleRejectCancel}
            onSubmit={handleRejectSubmit}
            isSubmitting={statusUpdating}
          />

          <ConfirmationModal
            isOpen={deleteModalOpen}
            title="Delete User"
            message={`Are you sure you want to delete ${clientToDelete?.name}? This action cannot be undone.`}
            confirmText="Delete"
            cancelText="Cancel"
            onConfirm={handleConfirmDelete}
            onCancel={handleCancelDelete}
            type="danger"
            loading={isDeleting}
            loadingText="Deleting user..."
          />

          <ClientDrawer
            open={drawerOpen}
            mode={drawerMode}
            selectedUser={selectedUser}
            formData={formData}
            onFormDataChange={setFormData}
            onClose={handleDrawerClose}
            onSave={handleSave}
            isSaving={isDrawerSaving}
          />

          <CreateClientDrawer
            open={createClientDrawerOpen}
            onClose={() => setCreateClientDrawerOpen(false)}
            onSuccess={() =>
              fetchUsers(
                searchTerm,
                activeFilters?.status || '',
                currentPage,
                pageSize
              )
            }
          />
        </div>
      </LoaderOverlay>
    </>
  );
};

export default Clients;
